import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Users, UserPlus, Settings } from 'lucide-react';
import { useTeamData } from '../company/hooks/useTeamData';
import { useTeamActions } from '../company/hooks/useTeamActions';
import TeamMemberRow from '../company/components/TeamMemberRow';
import PendingInvitationRow from '../company/components/PendingInvitationRow';
import CompanyInvite from '../company/CompanyInvite';
import SuspensionDialog from '../company/components/SuspensionDialog';
import DataTableWrapper from '@/components/shared/DataTableWrapper';
import ConfirmationDialog from '@/components/admin/components/ConfirmationDialog';

export default function AdminTeamManagement({ isSuperuser }: { isSuperuser: boolean }) {
  const { members, pendingInvitations, isLoading, companyName, companyId, fetchTeamData } = useTeamData();
  
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'suspended' | 'deactivated' | 'pending'>('active');
  const [showInviteForm, setShowInviteForm] = useState(false);

  const setMembers = (members: any) => {
    // This will be handled by the useTeamData hook's internal state
    fetchTeamData();
  };

  const setPendingInvitations = (invitations: any) => {
    // This will be handled by the useTeamData hook's internal state  
    fetchTeamData();
  };

  const { 
    handleMakeAdmin, 
    handleRemoveAdmin, 
    handleDeactivateMember, 
    handleReactivateMember, 
    handleCancelInvitation, 
    handleResendInvitation,
    handleSuspendMember,
    handleUnsuspendMember,
    handleSuspensionComplete,
    suspensionDialog,
    setSuspensionDialog,
    confirmDialog,
    closeConfirmDialog
  } = useTeamActions(members, setMembers, setPendingInvitations, companyName, companyId);

  // Filter members by status
  const activeMembers = members.filter(member => member.status === 'active');
  const suspendedMembers = members.filter(member => member.status === 'suspended');
  const deactivatedMembers = members.filter(member => member.status === 'deactivated');
  
  const displayedMembers = (() => {
    switch (activeFilter) {
      case 'active':
        return activeMembers;
      case 'suspended':
        return suspendedMembers;
      case 'deactivated':
        return deactivatedMembers;
      case 'pending':
        return [];
      case 'all':
        return members;
      default:
        return activeMembers;
    }
  })();
  
  const totalTeamSize = members.length + pendingInvitations.length;

  // Determine if current user can manage members (admin or owner)
  const canManageMembers = true; // Admin dashboard assumes management permissions

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Management
          </CardTitle>
          <CardDescription>Manage team members, roles, and invitations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-2">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Management - {companyName}
          </CardTitle>
          <CardDescription>
            Manage team members, roles, and invitations ({totalTeamSize} total)
          </CardDescription>
          <div>
            <Button
              variant="outline"
              onClick={() => setShowInviteForm(!showInviteForm)}
              className="flex items-center gap-2 w-full sm:w-auto"
            >
              <UserPlus className="h-4 w-4" />
              {showInviteForm ? 'Hide' : 'Invite'} Members
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Invitation Section */}
        {showInviteForm && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Invite New Team Members</CardTitle>
            </CardHeader>
            <CardContent>
              <CompanyInvite onInvitationSent={() => {
                fetchTeamData();
                setShowInviteForm(false);
              }} />
            </CardContent>
          </Card>
        )}

        {/* Team Statistics - Clickable Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card 
            className={`cursor-pointer transition-all hover:shadow-md ${
              activeFilter === 'active' ? 'ring-2 ring-green-500 shadow-md' : ''
            }`}
            onClick={() => setActiveFilter('active')}
          >
            <CardContent className="p-4 sm:p-4">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-green-100 text-green-800">
                  {activeMembers.length}
                </Badge>
                <span className="text-sm text-muted-foreground">Active Members</span>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all hover:shadow-md ${
              activeFilter === 'suspended' ? 'ring-2 ring-yellow-500 shadow-md' : ''
            }`}
            onClick={() => setActiveFilter('suspended')}
          >
            <CardContent className="p-4 sm:p-4">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                  {suspendedMembers.length}
                </Badge>
                <span className="text-sm text-muted-foreground">Suspended</span>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all hover:shadow-md ${
              activeFilter === 'deactivated' ? 'ring-2 ring-red-500 shadow-md' : ''
            }`}
            onClick={() => setActiveFilter('deactivated')}
          >
            <CardContent className="p-4 sm:p-4">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-red-100 text-red-800">
                  {deactivatedMembers.length}
                </Badge>
                <span className="text-sm text-muted-foreground">Deactivated</span>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all hover:shadow-md ${
              activeFilter === 'pending' ? 'ring-2 ring-blue-500 shadow-md' : ''
            }`}
            onClick={() => setActiveFilter('pending')}
          >
            <CardContent className="p-4 sm:p-4">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                  {pendingInvitations.length}
                </Badge>
                <span className="text-sm text-muted-foreground">Pending Invites</span>
              </div>
            </CardContent>
          </Card>
        </div>


        {/* Team Members Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Team Members
              {(activeFilter === 'pending' ? pendingInvitations.length : displayedMembers.length) > 0 && (
                <Badge variant="secondary">
                  {activeFilter === 'pending' ? pendingInvitations.length : displayedMembers.length} {
                    activeFilter === 'active' ? 'active' :
                    activeFilter === 'suspended' ? 'suspended' :
                    activeFilter === 'deactivated' ? 'deactivated' :
                    activeFilter === 'pending' ? 'pending' : 'total'
                  }
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <DataTableWrapper
              isLoading={false}
              isEmpty={activeFilter === 'pending' 
                ? pendingInvitations.length === 0 
                : displayedMembers.length === 0}
              emptyState={{
                title: activeFilter === 'pending' 
                  ? "No pending invitations" 
                  : `No ${activeFilter === 'all' ? '' : activeFilter + ' '}team members found`,
                description: activeFilter === 'pending'
                  ? "Invite new team members to get started."
                  : undefined
              }}
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead className="hidden sm:table-cell">Email</TableHead>
                    <TableHead className="hidden md:table-cell">Role</TableHead>
                    <TableHead className="hidden lg:table-cell">System Role</TableHead>
                    <TableHead>Status</TableHead>
                    {activeFilter === 'deactivated' && <TableHead className="hidden md:table-cell">Deactivated</TableHead>}
                    {canManageMembers && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeFilter === 'pending' ? (
                    pendingInvitations.map((invitation) => (
                      <PendingInvitationRow
                        key={invitation.id}
                        invitation={invitation}
                        canManageMembers={canManageMembers}
                        onResendInvitation={handleResendInvitation}
                        onCancelInvitation={handleCancelInvitation}
                      />
                    ))
                  ) : (
                    displayedMembers.map((member) => (
                      <TeamMemberRow
                        key={member.id}
                        member={member}
                        canManageMembers={canManageMembers}
                        showDeactivated={activeFilter === 'deactivated'}
                        onMakeAdmin={() => handleMakeAdmin(member.id, `${member.first_name} ${member.last_name}`.trim())}
                        onRemoveAdmin={() => handleRemoveAdmin(member.id, `${member.first_name} ${member.last_name}`.trim())}
                        onRemoveMember={() => handleDeactivateMember(member.id)}
                        onReactivateMember={() => handleReactivateMember(member.id)}
                        onSuspendMember={() => handleSuspendMember(member.id, `${member.first_name} ${member.last_name}`.trim())}
                        onUnsuspendMember={() => handleUnsuspendMember(member.id, `${member.first_name} ${member.last_name}`.trim())}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </DataTableWrapper>
          </CardContent>
        </Card>
      </CardContent>

      {/* Suspension Dialog */}
      <SuspensionDialog
        isOpen={suspensionDialog.isOpen}
        onClose={() => setSuspensionDialog(prev => ({ ...prev, isOpen: false }))}
        memberId={suspensionDialog.memberId}
        memberName={suspensionDialog.memberName}
        onSuspensionComplete={handleSuspensionComplete}
      />

      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        description={confirmDialog.description}
        actionLabel="Confirm"
        onClose={closeConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
      />
    </Card>
  );
}